import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useActivity } from '../hooks/useActivity';
import StatusBadge from '../components/StatusBadge';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
}

const actionTypeLabels = {
  status_change: 'changed status',
  cluster_merge: 'merged clusters',
  cluster_split: 'split cluster',
  cluster_rename: 'renamed cluster',
  note_added: 'added note',
};

export function Activity() {
  const { actions, loading, error, hasMore, fetchActions } = useActivity();
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    fetchActions({ limit, offset: (page - 1) * limit });
  }, [page, fetchActions]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div style={{ marginBottom: 24 }}>
        <h1 className="text-2xl font-bold text-coros-text-primary">Activity Log</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          Recent status changes and decisions on feedback clusters
        </p>
      </div>

      {loading && (
        <div className="text-center py-12">
          <p className="text-coros-text-muted">Loading activity...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <p className="text-coros-status-error">{error}</p>
        </div>
      )}

      {!loading && !error && actions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-coros-text-muted">No activity recorded yet. Status changes will appear here.</p>
        </div>
      )}

      {!loading && actions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {actions.map((action) => (
            <div
              key={action.id}
              style={{
                padding: '14px 16px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                marginBottom: 2,
              }}
            >
              {/* Main row */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {/* Icon */}
                <div style={{
                  width: 28, height: 28, borderRadius: 7,
                  background: 'var(--bg-tertiary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 1,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                      {action.performed_by || 'System'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>
                      {actionTypeLabels[action.action_type] || action.action_type} on
                    </span>
                    <Link
                      to={`/clusters/${action.cluster_id}`}
                      style={{ fontWeight: 600, color: 'var(--coros-red)', textDecoration: 'none' }}
                    >
                      {action.cluster_name || `Cluster #${action.cluster_id}`}
                    </Link>
                  </div>

                  {/* Status change badges */}
                  {action.action_type === 'status_change' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      {action.old_value && <StatusBadge status={action.old_value} size="xs" />}
                      {action.old_value && action.new_value && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>
                      )}
                      {action.new_value && <StatusBadge status={action.new_value} size="xs" />}
                    </div>
                  )}

                  {/* Note */}
                  {action.note && (
                    <p style={{
                      fontSize: 12, color: 'var(--text-secondary)',
                      margin: '6px 0 0', fontStyle: 'italic',
                      borderLeft: '2px solid var(--border-color)',
                      paddingLeft: 8,
                    }}>
                      "{action.note}"
                    </p>
                  )}
                </div>

                {/* Timestamp */}
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }}>
                  {timeAgo(action.performed_at)}
                </span>
              </div>
            </div>
          ))}

          {/* Pagination */}
          <div className="flex items-center justify-center space-x-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-coros-text-muted px-4 text-sm">
              Page {page}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Activity;
